import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { machineryService } from '../../../services/machineryService';
import Toast from '../../../components/Common/Toast';
import { getCurrentTimeLima, getCurrentDateLima } from '../../../utils/dateUtils';

const EVENT_TYPES = {
  inicio_preventivo: 'Inicio de mantenimiento preventivo',
  inicio_emergencia: 'Inicio de mantenimiento de emergencia',
  fin_preventivo: 'Fin de mantenimiento preventivo',
  fin_emergencia: 'Fin de mantenimiento de emergencia'
};

const MaintenanceAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('inicio'); // 'inicio' | 'cierre'
  const [modalItem, setModalItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    tipo: 'preventivo',
    event_date: getCurrentDateLima(),
    event_time: getCurrentTimeLima(),
    hours: '',
    kilometers: ''
  });

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const response = await machineryService.getMaintenanceAlerts();
      if (response.success && Array.isArray(response.data)) {
        setAlerts(response.data);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cargar alertas', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar alertas', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openMandarMantenimiento = (item) => {
    setModalItem(item);
    setModalMode('inicio');
    setForm({
      tipo: 'preventivo',
      event_date: getCurrentDateLima(),
      event_time: getCurrentTimeLima(),
      hours: item.unit === 'horas' && item.current_reading != null ? String(item.current_reading) : '',
      kilometers: item.unit === 'km' && item.current_reading != null ? String(item.current_reading) : ''
    });
    setModalOpen(true);
  };

  const openCerrarMantenimiento = (item) => {
    setModalItem(item);
    setModalMode('cierre');
    setForm({
      tipo: 'preventivo',
      event_date: getCurrentDateLima(),
      event_time: getCurrentTimeLima(),
      hours: item.unit === 'horas' && item.current_reading != null ? String(item.current_reading) : '',
      kilometers: item.unit === 'km' && item.current_reading != null ? String(item.current_reading) : ''
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalItem(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!modalItem) return;
    const isVehicle = modalItem.type === 'vehicle';
    const eventType = modalMode === 'inicio'
      ? (form.tipo === 'preventivo' ? EVENT_TYPES.inicio_preventivo : EVENT_TYPES.inicio_emergencia)
      : (form.tipo === 'preventivo' ? EVENT_TYPES.fin_preventivo : EVENT_TYPES.fin_emergencia);

    const payload = {
      event_date: form.event_date,
      event_time: form.event_time || null,
      event_type: eventType,
      machine_id: isVehicle ? null : modalItem.id,
      vehicle_id: isVehicle ? modalItem.id : null,
      hours: null,
      kilometers: null
    };
    if (modalItem.unit === 'horas' && form.hours !== '' && form.hours != null) {
      payload.hours = parseFloat(form.hours);
    }
    if (modalItem.unit === 'km' && form.kilometers !== '' && form.kilometers != null) {
      payload.kilometers = Math.round(parseFloat(String(form.kilometers).replace(',', '.')) * 100) / 100;
    }

    setSubmitting(true);
    try {
      const eventRes = await machineryService.createEvent(payload);
      if (!eventRes.success) {
        setToast({ isOpen: true, message: eventRes.message || 'Error al crear evento en bitácora', type: 'error' });
        setSubmitting(false);
        return;
      }
      const newStatus = modalMode === 'inicio' ? 'maintenance' : 'active';
      const getRes = isVehicle
        ? await machineryService.getVehicle(modalItem.id)
        : await machineryService.getMachine(modalItem.id);
      if (!getRes.success || !getRes.data) {
        setToast({ isOpen: true, message: getRes.message || 'Error al cargar datos del equipo', type: 'error' });
        setSubmitting(false);
        return;
      }
      const fullData = { ...getRes.data, status: newStatus };
      if (modalMode === 'cierre') {
        if (modalItem.unit === 'km' && form.kilometers !== '' && form.kilometers != null) {
          fullData.last_maintenance_km = Math.round(parseFloat(String(form.kilometers).replace(',', '.')));
        }
        if (modalItem.unit === 'horas' && form.hours !== '' && form.hours != null) {
          fullData.last_maintenance_hours = parseFloat(form.hours);
        }
      }
      const updateRes = isVehicle
        ? await machineryService.updateVehicle(modalItem.id, fullData)
        : await machineryService.updateMachine(modalItem.id, fullData);
      if (!updateRes.success) {
        setToast({ isOpen: true, message: updateRes.message || 'Error al actualizar estado', type: 'error' });
        setSubmitting(false);
        return;
      }
      setToast({
        isOpen: true,
        message: modalMode === 'inicio' ? 'Enviado a mantenimiento. Evento registrado en bitácora.' : 'Mantenimiento cerrado. Evento registrado en bitácora.',
        type: 'success'
      });
      closeModal();
      loadAlerts();
    } catch (err) {
      setToast({ isOpen: true, message: err.message || 'Error al procesar', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatReading = (value) => {
    if (value == null) return '—';
    return Number(value).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getStatusBadge = (item) => {
    if (item.asset_status === 'maintenance') {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">En mantenimiento</span>;
    }
    const map = {
      ok: { label: 'Ok', className: 'bg-green-100 text-green-800' },
      pronto: { label: 'Pronto', className: 'bg-amber-100 text-amber-800' },
      alerta: { label: 'Alerta', className: 'bg-red-100 text-red-800' }
    };
    const s = map[item.status] || { label: item.status, className: 'bg-gray-100 text-gray-800' };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${s.className}`}>{s.label}</span>;
  };

  const getProximaAlertaLabel = (item) => {
    if (item.asset_status === 'maintenance') {
      return '—';
    }
    if (item.unit === 'km' && item.remaining_km != null) return `${formatReading(item.remaining_km)} km`;
    if (item.unit === 'horas' && item.remaining_hours != null) return `${formatReading(item.remaining_hours)} h`;
    if (item.status === 'alerta') return '¡Ahora!';
    return `en ${formatReading(item.next_alert_at)} ${item.unit}`;
  };

  const editPath = (item) => item.type === 'vehicle' ? `/machinery/vehicles/${item.id}/edit` : `/machinery/machines/${item.id}/edit`;
  const isEnMantenimiento = (item) => item.asset_status === 'maintenance';

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Alertas de mantenimiento</h1>
        <p className="text-gray-600 mt-1">
          El sistema avisa según el kilometraje (vehículos) o las horas (máquinas) registrados en la bitácora.
          Configure en cada vehículo o máquina el intervalo de alerta (ej. cada 50 000 km) para que aparezcan aquí.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Link
          to="/machinery/vehicles"
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
        >
          Configurar vehículos
        </Link>
        <Link
          to="/machinery/machines"
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
        >
          Configurar máquinas
        </Link>
        <Link
          to="/machinery/maintenance/history"
          className="px-4 py-2 border border-teal-600 rounded-lg text-teal-700 bg-teal-50 hover:bg-teal-100 text-sm font-medium transition-colors"
        >
          Ver historial de mantenimientos
        </Link>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
          <p className="mt-2 text-gray-600">Cargando alertas...</p>
        </div>
      ) : alerts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-gray-600 mb-2">No hay alertas configuradas.</p>
          <p className="text-sm text-gray-500">
            En <Link to="/machinery/vehicles" className="text-teal-600 hover:underline">Vehículos</Link> o{' '}
            <Link to="/machinery/machines" className="text-teal-600 hover:underline">Máquinas</Link>, edite un ítem y defina
            &quot;Intervalo de alerta&quot; (ej. 50 000 km para vehículos o 500 horas para máquinas) y active el control de kilometraje u horómetro.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Lectura actual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Intervalo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Próxima alerta en</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {alerts.map((item) => (
                  <tr key={`${item.type}-${item.id}-${item.unit}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {item.type === 'vehicle' ? 'Vehículo' : 'Máquina'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.code}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{item.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatReading(item.current_reading)} {item.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatReading(item.interval)} {item.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {getProximaAlertaLabel(item)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(item)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <span className="flex items-center justify-end gap-2 flex-wrap">
                        <Link to={editPath(item)} className="text-teal-600 hover:text-teal-900">
                          Configurar
                        </Link>
                        {isEnMantenimiento(item) ? (
                          <button
                            type="button"
                            onClick={() => openCerrarMantenimiento(item)}
                            className="text-amber-700 hover:text-amber-900 font-medium"
                          >
                            Cerrar mantenimiento
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openMandarMantenimiento(item)}
                            className="text-teal-600 hover:text-teal-900 font-medium"
                          >
                            Mandar a mantenimiento
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Mandar a mantenimiento / Cerrar mantenimiento */}
      {modalOpen && modalItem && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={closeModal} aria-hidden="true" />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {modalMode === 'inicio' ? 'Mandar a mantenimiento' : 'Cerrar mantenimiento'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {modalItem.code} — {modalItem.name}
              </p>
              <form onSubmit={handleModalSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de mantenimiento</label>
                  <select
                    name="tipo"
                    value={form.tipo}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="preventivo">Preventivo</option>
                    <option value="emergencia">Emergencia</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
                    <input
                      type="date"
                      name="event_date"
                      value={form.event_date}
                      onChange={handleFormChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                    <input
                      type="time"
                      name="event_time"
                      value={form.event_time}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                {modalItem.unit === 'horas' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Horómetro (horas)</label>
                    <input
                      type="number"
                      name="hours"
                      value={form.hours}
                      onChange={handleFormChange}
                      step="0.01"
                      min="0"
                      placeholder="Ej. 2450,50"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                )}
                {modalItem.unit === 'km' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kilometraje (km)</label>
                    <input
                      type="number"
                      name="kilometers"
                      value={form.kilometers}
                      onChange={handleFormChange}
                      step="0.01"
                      min="0"
                      placeholder="Ej. 125000,25"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Se creará un evento en la bitácora y el estado del equipo se actualizará automáticamente.
                </p>
                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg font-medium"
                  >
                    {submitting ? 'Procesando...' : (modalMode === 'inicio' ? 'Enviar a mantenimiento' : 'Cerrar mantenimiento')}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default MaintenanceAlerts;
